// A note a family member shared with me (Carla's decision 4, 2026-10-12): read-only. Nothing here
// can be edited, and nothing is sent but the picture links. The writing is someone else's, so its
// HTML is sanitised before it is shown (a shared note must not be able to run anything here).
import DOMPurify from "dompurify";
import { NoteImage } from "./BlockEditor";
import { coverName } from "./noteCovers";
import { v } from "../../ui";
import "./notes.css";

const TEXT_TAGS = ['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'a', 'br', 'span', 'code', 'mark'];
const clean = (html) => DOMPurify.sanitize(html || '', { ALLOWED_TAGS: TEXT_TAGS, ALLOWED_ATTR: ['href', 'target', 'rel'] });
const Text = ({ html, className }) => <div className={`nt-ce ${className}`} dangerouslySetInnerHTML={{ __html: clean(html) }} />;
// Only a picture stored inside an older note, or a secure address; anything else is not shown.
const safeSrc = (src) => (/^data:image\/(png|jpe?g|gif|webp);base64,/i.test(src || '') || /^https:\/\//i.test(src || '') ? src : null);

function ReadBlock({ block, imageLink, pageInfo, onOpenPage }) {
  switch (block.type) {
    case 'divider': return <div className="nt-b-divider" role="separator"><span /></div>;
    case 'image': {
      const legacy = safeSrc(block.src);
      if (!block.fileId && !legacy) return null;
      return (
        <figure className="nt-b-image nt-b-image__frame">
          {block.fileId ? <NoteImage fileId={block.fileId} alt={block.alt} imageLink={imageLink} /> : <img src={legacy} alt={block.alt || 'Image'} />}
          {block.alt && <figcaption className="nt-b-image__caption">{block.alt}</figcaption>}
        </figure>
      );
    }
    case 'page': {
      const info = pageInfo(block.pageId);
      return info ? (
        <button type="button" className="nt-b-page" aria-label={`Open page: ${info.title}`} onClick={() => onOpenPage(block.pageId)}>
          <span aria-hidden="true">{info.emoji || '📄'}</span> {info.title}
        </button>
      ) : <span className="nt-b-page nt-b-page--gone"><span aria-hidden="true">📄</span> A page inside, not shared with you</span>;
    }
    case 'checkbox':
      return (
        <div className={`nt-b-todo${block.checked ? ' nt-b-todo--done' : ''}`}>
          <span className="nt-check nt-check--read" role="img" aria-label={block.checked ? 'Done' : 'Not done'} data-checked={block.checked ? 'true' : 'false'} />
          <Text html={block.html} className="nt-ce--todo" />
        </div>
      );
    case 'callout':
      return <div className="nt-b-callout"><span aria-hidden="true">💡</span><Text html={block.html} className="nt-ce--callout" /></div>;
    case 'bullet':
    case 'numbered':
      return (
        <div className="nt-b-list">
          <span className="nt-b-list__mark" aria-hidden="true">{block.type === 'bullet' ? '•' : '1.'}</span>
          <Text html={block.html} className="nt-ce--list" />
        </div>
      );
    default: {
      const type = ['h1', 'h2', 'h3', 'quote', 'code'].includes(block.type) ? block.type : 'paragraph';
      return <Text html={block.html} className={`nt-ce--${type}`} />;
    }
  }
}

// The note itself, read only: its cover, icon, title and blocks. The Notes page's own note view,
// used here for a note shared with me and by the Dashboard's Notes tile (Carla's item 6), so a note
// reads the same wherever it is opened.
export function NoteSheet({ note, imageLink, pageInfo = () => null, onOpenPage = () => {}, coverFallback = 'pastel-pink' }) {
  const title = note.title?.trim() || 'Untitled';
  const blocks = Array.isArray(note.blocks) && note.blocks.length
    ? note.blocks
    : (note.text || '').split('\n').map((l, i) => ({ id: `t${i}`, type: 'paragraph', html: l.replace(/&/g, '&amp;').replace(/</g, '&lt;') }));
  return (
    <article className="nt-sheet">
      <div className="nt-cover" style={{ '--nt-cover': coverName(note.cover) ? v(note.cover) : v(coverFallback) }} aria-hidden="true" />
      <div className="nt-sheet__body">
        <div className="nt-page">
          <div className="nt-page__icon"><span className="nt-icon-tile nt-icon-tile--read" aria-hidden="true">{note.emoji || '📄'}</span></div>
          <h2 className="nt-title nt-title--read">{title}</h2>
          <div className="nt-blocks nt-blocks--read">
            {blocks.map((b, i) => <ReadBlock key={b.id || i} block={b} imageLink={imageLink} pageInfo={pageInfo} onOpenPage={onOpenPage} />)}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function SharedNote({ note, phone, onClose, imageLink, pageInfo, onOpenPage }) {
  const title = note.title?.trim() || 'Untitled';
  return (
    <div className="nt-editor nt-editor--read">
      <div className="nt-bar">
        <nav className="nt-crumbs" aria-label="Breadcrumb">
          <button type="button" className="nt-crumbs__back" onClick={onClose}>← Notes</button>
          <span className="nt-crumbs__sep" aria-hidden="true">/</span>
          <span className="nt-crumbs__step">Shared with me</span>
          <span className="nt-crumbs__sep" aria-hidden="true">/</span>
          <span className="nt-crumbs__here" aria-current="page">{note.emoji ? `${note.emoji} ` : ''}{title}</span>
        </nav>
      </div>
      <p className="nt-shared-by">Shared with you by {note.sharedByName}. You can read it; only they can change it.</p>
      <NoteSheet note={note} imageLink={imageLink} pageInfo={pageInfo} onOpenPage={onOpenPage} />
    </div>
  );
}
